import { c as _c } from "react/compiler-runtime";
import LinkComponent, { LinkComponentLink } from '../link/link.component';
import NavComponent, { NavigationData } from '../nav/nav.component';
import { ReactElement, memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Address } from 'next-seo/lib/types';
import BurgerIconComponent from '@spa/components/burger-icon/burger-icon.component';
import { CmsViewMode } from '@spa/generated/graphql/graphql';
import ConfigurableCmsComponentMetadata from '../../top-level-components/cms-page/metadata/configurable/configurable-cms-component-metadata.component';
import ConfigurableCmsSlotMetadata from '../../top-level-components/cms-page/metadata/configurable/configurable-cms-slot-metadata.component';
import IconListComponent from '../icon-list/icon-list.component';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import { Metadata } from '@spa/top-level-components/cms-page/metadata/Metadata';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { NavigationApiData } from '@spa/app/api/navigation/route';
import { OrganizationJsonLd } from 'next-seo';
import SearchBarComponent from '../search-bar/search-bar.component';
import classNames from 'clsx';
import { isDisplayOrShopStoreType } from '@spa/lib/configs/storeType';
import logger from '@spa/lib/logger';
import selectBuildId from '@spa/lib/store/settings/selectors/selectBuildId';
import selectTotalQuantityCount from '../../lib/store/carts/selectors/selectTotalQuantityCount';
import selectWishlistTotalQuantityCount from '@spa/lib/store/wishlists/selectors/selectWishlistTotalQuantityCount';
import styles from './header.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import useBodyOverflowHidden from '@spa/lib/react/hooks/useBodyOverflowHidden';
import useFetch from '@spa/lib/react/hooks/useFetch';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import { usePathname } from 'next/navigation';
import { useSelector } from 'react-redux';
export interface HeaderNavigationEntry {
  readonly link: LinkComponentLink;
  readonly text: string;
  readonly metadata: ReadonlyArray<Metadata>;
}
export interface HeaderIconNavigationEntry extends HeaderNavigationEntry {
  readonly icon: Icon;
}
interface Icon {
  readonly src: string;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
  readonly mimeType: string;
}
export interface ImageCms {
  readonly src: string;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
}
export interface Props {
  readonly id: string;
  readonly logo: ImageCms | null;
  readonly iconNavigationEntries: ReadonlyArray<HeaderIconNavigationEntry>;
  readonly metaNavigationEntries: ReadonlyArray<HeaderNavigationEntry>;
  readonly quickNavigationEntries: ReadonlyArray<HeaderIconNavigationEntry>;
  readonly iconNavigationEntriesMetadata: ReadonlyArray<Metadata>;
  readonly metaNavigationEntriesMetadata: ReadonlyArray<Metadata>;
  readonly quickNavigationEntriesMetadata: ReadonlyArray<Metadata>;
  readonly editorMode: boolean;
  readonly previewMode: boolean;
  readonly navigation: NavigationData | null;
}
interface HeaderContentProps extends Props {
  readonly cartTotalQuantityCount: number;
  readonly formatMessage: NamespacedIntl['formatMessage'];
  readonly navigationData: NavigationData | null;
  readonly reducedView: boolean;
  readonly wishlistTotalQuantityCount: number;
}
const HeaderComponent = props => {
  const $ = _c(17);
  const {
    editorMode,
    navigation,
    previewMode
  } = props;
  const pathname = usePathname();
  const {
    formatMessage
  } = useNamespacedIntl("header");
  const cartTotalQuantityCount = useSelector(selectTotalQuantityCount);
  const wishlistTotalQuantityCount = useSelector(selectWishlistTotalQuantityCount);
  const buildId = useSelector(selectBuildId);
  let t0;
  if ($[0] !== buildId || $[1] !== editorMode || $[2] !== previewMode) {
    t0 = () => {
      const queryParams = new URLSearchParams();
      if (buildId !== null) {
        queryParams.set("buildId", buildId);
      }
      if (editorMode) {
        queryParams.set("viewMode", CmsViewMode.EDITOR);
      } else {
        if (previewMode) {
          queryParams.set("viewMode", CmsViewMode.PREVIEW);
        }
      }
      const queryString = queryParams.toString();
      return `/api/navigation/${queryString === "" ? "" : `?${queryString}`}`;
    };
    $[0] = buildId;
    $[1] = editorMode;
    $[2] = previewMode;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const navigationUrl = t0;
  const fetchResult = useFetch(navigationUrl());
  const navigationData = fetchResult.type === "succeeded" ? fetchResult.data : navigation;
  const reducedView = fetchResult.type !== "succeeded";
  let t1;
  if ($[4] !== fetchResult.statusCode || $[5] !== fetchResult.type) {
    t1 = () => {
      if (fetchResult.type === "failed") {
        if (fetchResult.statusCode === 403) {
          logger.warn("Failed to fetch navigation data (403). Will reload now to trigger the PoW.");
          window.location.reload();
        } else {
          logger.warn("Failed to fetch navigation data");
        }
      }
    };
    $[4] = fetchResult.statusCode;
    $[5] = fetchResult.type;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== fetchResult) {
    t2 = [fetchResult];
    $[7] = fetchResult;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] !== cartTotalQuantityCount || $[10] !== formatMessage || $[11] !== navigationData || $[12] !== pathname || $[13] !== props || $[14] !== reducedView || $[15] !== wishlistTotalQuantityCount) {
    t3 = <HeaderContentComponent key={pathname} {...props} cartTotalQuantityCount={cartTotalQuantityCount} formatMessage={formatMessage} navigationData={navigationData} reducedView={reducedView} wishlistTotalQuantityCount={wishlistTotalQuantityCount} />;
    $[9] = cartTotalQuantityCount;
    $[10] = formatMessage;
    $[11] = navigationData;
    $[12] = pathname;
    $[13] = props;
    $[14] = reducedView;
    $[15] = wishlistTotalQuantityCount;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
const HeaderContentComponent = t0 => {
  const $ = _c(116);
  const {
    id,
    editorMode,
    logo,
    iconNavigationEntries,
    iconNavigationEntriesMetadata,
    metaNavigationEntries,
    metaNavigationEntriesMetadata,
    previewMode,
    quickNavigationEntries,
    quickNavigationEntriesMetadata,
    cartTotalQuantityCount,
    formatMessage,
    navigationData,
    reducedView,
    wishlistTotalQuantityCount
  } = t0;
  const [isNavMobileActive, setIsNavMobileActive] = useState(false);
  const [isBurgerActive, setIsBurgerActive] = useState(false);
  const [isMobileWasActive, setIsMobileWasActive] = useState(false);
  const headerRef = useRef(null);
  useBodyOverflowHidden(isNavMobileActive);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const headerElement = headerRef.current;
      if (!headerElement) {
        return;
      }
      const bottom = headerElement.getBoundingClientRect().bottom;
      headerElement.style.setProperty("--header-height", `${bottom}px`);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const updateHeaderHeight = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const headerElement_0 = headerRef.current;
      if (!headerElement_0) {
        return;
      }
      updateHeaderHeight();
      const observedElement = headerElement_0.closest("header") ?? headerElement_0;
      const observer = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      observer.observe(observedElement);
      return () => {
        observer.disconnect();
      };
    };
    t3 = [updateHeaderHeight];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = isMobileActive => {
      setIsBurgerActive(isMobileActive);
      if (isMobileActive) {
        setIsMobileWasActive(true);
        updateHeaderHeight();
      }
      startTransition(() => {
        setIsNavMobileActive(isMobileActive);
      });
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const changeNavStatusMobile = t4;
  let t5;
  if ($[4] !== isBurgerActive || $[5] !== isMobileWasActive) {
    t5 = classNames(styles.header, {
      [styles["header--mobile-active"]]: isBurgerActive,
      [styles["header--mobile-was-active"]]: isMobileWasActive
    });
    $[4] = isBurgerActive;
    $[5] = isMobileWasActive;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const headerClasses = t5;
  const showPreview = editorMode || previewMode;
  let t6;
  if ($[7] !== editorMode) {
    t6 = (items, t7) => {
      const renderIcon = t7 === undefined ? false : t7;
      return <div className={styles["header__meta-nav-items"]}>{items.map(item => <div className={styles["header__meta-nav-item-wrapper"]} key={item.text}>{editorMode && <ConfigurableCmsComponentMetadata beforeComponent={true} metadata={item.metadata} />}<LinkComponent link={item.link} onClick={event => {
            tracker.navigationClick({
              click_name: item.text,
              click_position: "meta_navi",
              click_url: event.currentTarget.href
            });
          }}><div className={classNames(styles["header__meta-nav-item"], "text-xs")}>{renderIcon && "icon" in item && <ImageComponent className={styles["header__meta-nav-item-icon"]} src={item.icon.src} alt={`${item.icon.alt}`} width={item.icon.width} height={item.icon.height} mimeType={item.icon.mimeType} />}<span>{item.text}</span></div></LinkComponent>{editorMode && <ConfigurableCmsComponentMetadata beforeComponent={false} metadata={item.metadata} />}</div>)}</div>;
    };
    $[7] = editorMode;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const renderNavItems = t6;
  let t7;
  if ($[9] !== formatMessage) {
    t7 = formatMessage("richSnippets.address.streetAddress");
    $[9] = formatMessage;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== formatMessage) {
    t8 = formatMessage("richSnippets.address.addressLocality");
    $[11] = formatMessage;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== formatMessage) {
    t9 = formatMessage("richSnippets.address.addressCountry");
    $[13] = formatMessage;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== formatMessage) {
    t10 = formatMessage("richSnippets.address.addressRegion");
    $[15] = formatMessage;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== formatMessage) {
    t11 = formatMessage("richSnippets.address.postalCode");
    $[17] = formatMessage;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t12 = {
      streetAddress: t7,
      addressLocality: t8,
      addressCountry: t9,
      addressRegion: t10,
      postalCode: t11
    };
    $[19] = t10;
    $[20] = t11;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const jsonLdAddress = t12;
  let t13;
  if ($[25] !== quickNavigationEntries || $[26] !== renderNavItems) {
    t13 = renderNavItems(quickNavigationEntries, true);
    $[25] = quickNavigationEntries;
    $[26] = renderNavItems;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const quickNavigationEntriesElm = t13;
  let t14;
  if ($[28] !== metaNavigationEntries || $[29] !== renderNavItems) {
    t14 = renderNavItems(metaNavigationEntries);
    $[28] = metaNavigationEntries;
    $[29] = renderNavItems;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const metaNavigationEntriesElm = t14;
  let t15;
  if ($[31] !== isBurgerActive) {
    t15 = () => changeNavStatusMobile(!isBurgerActive);
    $[31] = isBurgerActive;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== formatMessage) {
    t16 = formatMessage("menuButton");
    $[33] = formatMessage;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== formatMessage) {
    t17 = formatMessage("menuButton");
    $[35] = formatMessage;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== isBurgerActive) {
    t18 = <BurgerIconComponent activated={isBurgerActive} />;
    $[37] = isBurgerActive;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== isBurgerActive || $[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18) {
    t19 = <button className={styles["header__burger-btn"]} onClick={t15} title={t16} aria-label={t17} aria-expanded={isBurgerActive} aria-controls="navigation">{t18}</button>;
    $[39] = isBurgerActive;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== formatMessage || $[46] !== jsonLdAddress || $[47] !== logo || $[48] !== showPreview) {
    t20 = logo && <div className={styles["header__header-bar-logo"]}><OrganizationJsonLd useAppDir={true} address={jsonLdAddress} url={formatMessage("richSnippets.url")} sameAs={formatMessage("richSnippets.sameAs").split(" | ")} logo={formatMessage("richSnippets.logoUrl")} name={formatMessage("richSnippets.name")} description={formatMessage("richSnippets.description")} email={formatMessage("richSnippets.email")} telephone={formatMessage("richSnippets.telephone")} vatID={formatMessage("richSnippets.vatID")} /><Link prefetch={false} className={styles["header__header-bar-logo-link"]} href="/" onClick={event_0 => {
        tracker.navigationClick({
          click_name: logo.alt,
          click_position: "icon_navi",
          click_url: event_0.currentTarget.href
        });
      }}><ImageComponent className={styles["header__header-bar-logo-image"]} src={logo.src} alt={logo.alt} width={logo.width} height={logo.height} sizes="(max-width: 767px) 79px, 186px" priority={false} />{showPreview && <div className={`${styles.header__preview} text-s-immutable`}>PREVIEW</div>}</Link></div>;
    $[45] = formatMessage;
    $[46] = jsonLdAddress;
    $[47] = logo;
    $[48] = showPreview;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== t19 || $[51] !== t20) {
    t21 = <div className={styles["header-wrapper__menu-and-logo"]}>{t19}{t20}</div>;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== id) {
    t22 = isDisplayOrShopStoreType && <div className={styles["header__header-bar-search-wrapper-desktop"]}><div className={styles["header__header-bar-search-desktop"]}><SearchBarComponent id={`${id}-searchbar`} /></div></div>;
    $[53] = id;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== cartTotalQuantityCount || $[56] !== editorMode || $[57] !== iconNavigationEntries || $[58] !== iconNavigationEntriesMetadata || $[59] !== wishlistTotalQuantityCount) {
    t23 = <div className={styles["header__header-bar-items-container"]}><IconListComponent cartTotalQuantityCount={cartTotalQuantityCount} editorMode={editorMode} icons={iconNavigationEntries} iconsMetadata={iconNavigationEntriesMetadata} wishlistTotalQuantityCount={wishlistTotalQuantityCount} /></div>;
    $[55] = cartTotalQuantityCount;
    $[56] = editorMode;
    $[57] = iconNavigationEntries;
    $[58] = iconNavigationEntriesMetadata;
    $[59] = wishlistTotalQuantityCount;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t21 || $[62] !== t22 || $[63] !== t23) {
    t24 = <div className={styles.header__main}>{t21}{t22}{t23}</div>;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== id) {
    t25 = isDisplayOrShopStoreType && <div className={styles["header__header-bar-search"]}><SearchBarComponent id={`${id}-searchbar-mobile`} /></div>;
    $[65] = id;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== t24 || $[68] !== t25) {
    t26 = <div className={styles["header__main-wrapper"]}>{t24}{t25}</div>;
    $[67] = t24;
    $[68] = t25;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  const mainWrapperElm = t26;
  let t27;
  if ($[70] !== editorMode || $[71] !== quickNavigationEntriesMetadata) {
    t27 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={quickNavigationEntriesMetadata} />;
    $[70] = editorMode;
    $[71] = quickNavigationEntriesMetadata;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== quickNavigationEntriesElm) {
    t28 = <div>{quickNavigationEntriesElm}</div>;
    $[73] = quickNavigationEntriesElm;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== editorMode || $[76] !== quickNavigationEntriesMetadata) {
    t29 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={quickNavigationEntriesMetadata} />;
    $[75] = editorMode;
    $[76] = quickNavigationEntriesMetadata;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t27 || $[79] !== t28 || $[80] !== t29) {
    t30 = <div className={styles["header__meta-nav-items"]}>{t27}{t28}{t29}</div>;
    $[78] = t27;
    $[79] = t28;
    $[80] = t29;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  let t31;
  if ($[82] !== editorMode || $[83] !== metaNavigationEntriesMetadata) {
    t31 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={true} metadata={metaNavigationEntriesMetadata} />;
    $[82] = editorMode;
    $[83] = metaNavigationEntriesMetadata;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== metaNavigationEntriesElm) {
    t32 = <div>{metaNavigationEntriesElm}</div>;
    $[85] = metaNavigationEntriesElm;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  let t33;
  if ($[87] !== editorMode || $[88] !== metaNavigationEntriesMetadata) {
    t33 = editorMode && <ConfigurableCmsSlotMetadata beforeSlot={false} metadata={metaNavigationEntriesMetadata} />;
    $[87] = editorMode;
    $[88] = metaNavigationEntriesMetadata;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== t31 || $[91] !== t32 || $[92] !== t33) {
    t34 = <div className={styles["header__meta-nav-links"]}>{t31}{t32}{t33}</div>;
    $[90] = t31;
    $[91] = t32;
    $[92] = t33;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  let t35;
  if ($[94] !== t30 || $[95] !== t34) {
    t35 = <div className={styles["header__meta-wrapper"]}><div className={styles.header__meta}>{t30}{t34}</div></div>;
    $[94] = t30;
    $[95] = t34;
    $[96] = t35;
  } else {
    t35 = $[96];
  }
  let t36;
  if ($[97] !== isNavMobileActive) {
    t36 = () => changeNavStatusMobile(!isNavMobileActive);
    $[97] = isNavMobileActive;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = open => changeNavStatusMobile(open);
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = [];
    $[100] = t38;
  } else {
    t38 = $[100];
  }
  let t39;
  if ($[101] !== editorMode || $[102] !== iconNavigationEntries || $[103] !== id || $[104] !== isNavMobileActive || $[105] !== metaNavigationEntries || $[106] !== navigationData || $[107] !== quickNavigationEntries || $[108] !== reducedView || $[109] !== t36) {
    t39 = <div className={styles["header__navigation-wrapper"]}><NavComponent id={id} onClick={t36} onSidebarVisibilityChange={t37} isNavMobileActive={isNavMobileActive} metadata={t38} editorMode={editorMode} iconNavigationItem={iconNavigationEntries} metaNavigationItem={metaNavigationEntries} quickNavigationItems={quickNavigationEntries} navigation={navigationData} reducedView={reducedView} /></div>;
    $[101] = editorMode;
    $[102] = iconNavigationEntries;
    $[103] = id;
    $[104] = isNavMobileActive;
    $[105] = metaNavigationEntries;
    $[106] = navigationData;
    $[107] = quickNavigationEntries;
    $[108] = reducedView;
    $[109] = t36;
    $[110] = t39;
  } else {
    t39 = $[110];
  }
  let t40;
  if ($[111] !== headerClasses || $[112] !== mainWrapperElm || $[113] !== t35 || $[114] !== t39) {
    t40 = <div ref={headerRef} id="header" className={headerClasses}>{t35}{mainWrapperElm}{t39}</div>;
    $[111] = headerClasses;
    $[112] = mainWrapperElm;
    $[113] = t35;
    $[114] = t39;
    $[115] = t40;
  } else {
    t40 = $[115];
  }
  return t40;
};
export default memo(HeaderComponent);